import { createEvent, createStore, sample } from 'effector'
import {
  NotificationPayload,
  showNotification,
} from '../../features/notifications'
import { fx, Request } from '../../libs/request'
import {
  WalletGuardWallet,
  WalletGuardWalletsRequest,
  WalletGuardWalletsResponse,
} from './types'

export const walletsRequested = createEvent<WalletGuardWalletsRequest>()

export const getWalletsFx = fx<void, WalletGuardWalletsResponse>({
  method: 'GET',
  url: '/wallet-guard/wallets',
})

const $latestRequest = createStore<Request<void> | null>(null).on(
  getWalletsFx,
  (_, params) => params,
)

export const $lastWalletsRequest =
  createStore<WalletGuardWalletsRequest | null>(null).on(
    walletsRequested,
    (_, params) => params,
  )

const walletsReceived = createEvent<WalletGuardWalletsResponse>()
const walletsFailed = createEvent()

export const $wallets = createStore<WalletGuardWallet[] | null>(null)
  .on(walletsReceived, (_, response) => response.items)
  .reset(getWalletsFx)

export const $walletsTotal = createStore(0).on(
  walletsReceived,
  (_, response) => response.total,
)

export const $walletsHasError = createStore(false)
  .on(walletsFailed, () => true)
  .reset(getWalletsFx)

sample({
  clock: walletsRequested,
  fn: ({ limit, page, orderBy, orderDirection, filters }) => {
    const query: Record<string, string> = {
      limit: String(limit),
      page: String(page),
    }
    const where: Record<string, string | string[]> = {}

    if (orderBy && orderDirection) {
      query.order = `${orderDirection === 'descend' ? '-' : ''}${orderBy}`
    }

    if (filters.address?.trim()) where.address = filters.address.trim()
    if (filters.name?.trim()) where.name = filters.name.trim()
    for (const key of [
      'currency',
      'type',
      'status',
      'paymentSystems',
    ] as const) {
      const values = filters[key]
      if (values?.length) where[key] = values
    }
    if (Object.keys(where).length) query.where = JSON.stringify(where)

    return { query }
  },
  target: getWalletsFx,
})

sample({
  clock: getWalletsFx.done,
  source: $latestRequest,
  filter: (latest, { params }) => latest === params,
  fn: (_, { result }) => result.body,
  target: walletsReceived,
})

sample({
  clock: getWalletsFx.fail,
  source: $latestRequest,
  filter: (latest, { params }) => latest === params,
  fn: () => undefined,
  target: walletsFailed,
})

export const walletBlockRequested = createEvent<number>()
export const walletUnblockRequested = createEvent<number>()

export const blockWalletFx = fx<void, void>({ method: 'POST' })
export const unblockWalletFx = fx<void, void>({ method: 'POST' })

// Which row is in flight — the table uses it to show a spinner on that
// button and to disable the others.
export const $pendingWalletId = createStore<number | null>(null)
  .on(walletBlockRequested, (_, id) => id)
  .on(walletUnblockRequested, (_, id) => id)
  .reset(blockWalletFx.finally)
  .reset(unblockWalletFx.finally)

sample({
  clock: walletBlockRequested,
  fn: (id) => ({ url: `/wallet-guard/wallets/${id}/block` }),
  target: blockWalletFx,
})

sample({
  clock: walletUnblockRequested,
  fn: (id) => ({ url: `/wallet-guard/wallets/${id}/unblock` }),
  target: unblockWalletFx,
})

sample({
  clock: [blockWalletFx.done, unblockWalletFx.done],
  source: $latestRequest,
  filter: (request): request is Request<void> => Boolean(request),
  target: getWalletsFx,
})

sample({
  clock: blockWalletFx.done,
  fn: (): NotificationPayload => ({
    type: 'success',
    description: 'Кошелёк заблокирован',
  }),
  target: showNotification,
})

sample({
  clock: unblockWalletFx.done,
  fn: (): NotificationPayload => ({
    type: 'success',
    description: 'Кошелёк разблокирован',
  }),
  target: showNotification,
})

sample({
  clock: [blockWalletFx.failData, unblockWalletFx.failData],
  fn: (error): NotificationPayload => ({
    type: 'error',
    description: error?.body?.message ?? 'Не удалось выполнить запрос',
  }),
  target: showNotification,
})
